import { useCallback, useSyncExternalStore, type CSSProperties } from 'react'
import { useAgentEffectsStore } from '@/app/providers/AgentEffectsContext'
import { summarizeAgentEffects } from '@/lib/agent-effects/announce'

/**
 * Single polite aria-live region that announces a coalesced summary of the
 * agent's recent visible actions (e.g. "Xavior created 1 event, updated 2
 * tasks"). One region for the whole app — bursts collapse into one utterance
 * rather than per-row spam. Announces type + count only (no titles/bodies).
 * Mounted once inside `AgentEffectsProvider`.
 */
const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

export function AgentEffectsLiveRegion() {
  const store = useAgentEffectsStore()
  const subscribe = useCallback((listener: () => void) => store.subscribeAll(listener), [store])
  const getSnapshot = useCallback(() => summarizeAgentEffects(store.list()), [store])
  const message = useSyncExternalStore(subscribe, getSnapshot, () => '')
  return (
    <div role="status" aria-live="polite" aria-atomic="true" style={SR_ONLY}>
      {message}
    </div>
  )
}
